import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiEye, FiSearch } from 'react-icons/fi';
import { getLabourPayouts, updateLabourPayoutStatus } from '../services/api';

const payoutStatuses = ['pending', 'processing', 'success', 'failed'];

const LabourPayouts: React.FC = () => {
  const [payouts, setPayouts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selectedPayout, setSelectedPayout] = useState<any>(null);
  const [form, setForm] = useState({ status: 'pending', transactionRef: '', failureReason: '' });

  const loadPayouts = useCallback(async (searchValue = search) => {
    try {
      setLoading(true);
      const res = await getLabourPayouts({
        page,
        pageSize: 20,
        status,
        search: searchValue,
      });
      setPayouts(res.data.data ?? []);
      setTotalCount(res.data.totalCount ?? 0);
    } catch (error) {
      console.error(error);
      toast.error('Unable to load labour cash transactions');
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  const handleSearch = () => {
    setPage(1);
    loadPayouts(search);
  };

  const openDetail = (payout: any) => {
    setSelectedPayout(payout);
    setForm({
      status: payout.status,
      transactionRef: payout.transactionRef || '',
      failureReason: payout.failureReason || '',
    });
  };

  const handleUpdate = async () => {
    try {
      await updateLabourPayoutStatus(selectedPayout.payoutId, form);
      toast.success('Payout updated');
      setSelectedPayout(null);
      loadPayouts();
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Unable to update payout');
    }
  };

  const totalPages = Math.ceil(totalCount / 20);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Labour Cash Transactions</h1>
        <p>Review labour reward payouts, search by user or UPI, and update payout status after payment processing.</p>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-filters">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by labour name, mobile, UPI, product or transaction ref..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <select className="filter-dropdown" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {payoutStatuses.map((item) => (
                <option key={item} value={item}>{item.charAt(0).toUpperCase() + item.slice(1)}</option>
              ))}
            </select>
            <button className="btn btn-outline" onClick={handleSearch}>
              <FiSearch /> Search
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading cash transactions...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Labour</th>
                  <th>Product</th>
                  <th>Amount</th>
                  <th>UPI</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <tr key={payout.payoutId}>
                    <td>
                      <div className="stack-sm">
                        <strong>{payout.fullName}</strong>
                        <span className="text-muted">{payout.mobile}</span>
                        {payout.city && <span className="text-muted">{payout.city}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="stack-sm">
                        <span>{payout.productName}</span>
                        <span className="text-muted">{payout.qrUuid}</span>
                      </div>
                    </td>
                    <td>₹{Number(payout.amount).toLocaleString('en-IN')}</td>
                    <td>{payout.upiId}</td>
                    <td>
                      <span className={`badge ${
                        payout.status === 'success'
                          ? 'badge-success'
                          : payout.status === 'failed'
                            ? 'badge-danger'
                            : payout.status === 'processing'
                              ? 'badge-info'
                              : 'badge-warning'
                      }`}>
                        {payout.status}
                      </span>
                    </td>
                    <td>{new Date(payout.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button className="btn btn-sm btn-outline" onClick={() => openDetail(payout)}>
                        <FiEye /> View
                      </button>
                    </td>
                  </tr>
                ))}
                {payouts.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center">No labour cash transactions found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {selectedPayout && (
        <div className="modal-overlay" onClick={() => setSelectedPayout(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Labour Cash Transaction</h3>
              <button className="modal-close" onClick={() => setSelectedPayout(null)}>&times;</button>
            </div>

            <div className="details-grid">
              <div className="detail-item">
                <label>Name</label>
                <span>{selectedPayout.fullName}</span>
              </div>
              <div className="detail-item">
                <label>Mobile</label>
                <span>{selectedPayout.mobile}</span>
              </div>
              <div className="detail-item">
                <label>Product</label>
                <span>{selectedPayout.productName}</span>
              </div>
              <div className="detail-item">
                <label>Amount</label>
                <span>₹{Number(selectedPayout.amount).toLocaleString('en-IN')}</span>
              </div>
              <div className="detail-item">
                <label>UPI ID</label>
                <span>{selectedPayout.upiId}</span>
              </div>
              <div className="detail-item">
                <label>QR</label>
                <span>{selectedPayout.qrUuid}</span>
              </div>
            </div>

            <h4 className="mt-20 mb-10">Update Payout Status</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <select className="form-control" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {payoutStatuses.map((item) => (
                    <option key={item} value={item}>{item.charAt(0).toUpperCase() + item.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Transaction Reference</label>
                <input className="form-control" value={form.transactionRef} onChange={(e) => setForm({ ...form, transactionRef: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Failure Reason</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.failureReason}
                onChange={(e) => setForm({ ...form, failureReason: e.target.value })}
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSelectedPayout(null)}>Close</button>
              <button className="btn btn-primary" onClick={handleUpdate}>Update Payout</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabourPayouts;
